import React, { useEffect, useState } from "react";
import {useParams, useNavigate, NavigateFunction} from "react-router-dom";
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {TextField, Link, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent} from "@mui/material";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import { ArrowBackIosNew } from "@mui/icons-material";
import showNotification from "../../utils/Notify";
import {Box, Grid, Typography, Button, Stack, Breadcrumbs} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CategoryIcon from '@mui/icons-material/Category';
import {update, create, getById} from "../../../services/category.service";
import ICategory from "../../../types/category.type";

const CategoryForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { id } = useParams();

  const initialValues: ICategory = {
    id: null,
    name: '',
    active: 'true',
  };

  const [category, setCategory] = useState(initialValues);

  const validationSchema = Yup.object({
    name: Yup.string().required('El nombre de la Categoria es un campo requerido'),
    active: Yup.string().required('Es necesario indicar si la Categoria esta activa'),
  });

  const onSubmit = (category: ICategory) => {
    if (id) {
      update(category.id, category)
        .then(() => {
          showNotification({type: 'success', message: 'Categoria actualizada correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(category)
        .then((response) => {
          showNotification({type: 'success', message: 'Categoria creada correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const goBack = () => {
    navigate(-1)
  };

  const handleChangeActive = (event: SelectChangeEvent) => {
    formik.setFieldValue('active', event.target.value)
  };

  const fetchCategory = async () => {
    if (id) {
      getById(id).then( (response) => {
        let categoryData = response.data;
        formik.setValues(categoryData)
        setCategory(categoryData)
      })
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <CategoryIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => goBack()}
                  sx={{ cursor: 'pointer' }}
                >
                  Categorias
                </Link>
              </Typography>
              <Typography level="h4" component="h1">
                {id ? "Editar" : "Nuevo"}
              </Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={12} md={12} sm={12} xs={12}>
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="name"
                      label="Nombre"
                      name="name"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                      error={!!(formik.touched.name && formik.errors.name)}
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <span className="Error-alert">{formik.errors.name}</span>
                    ) : null}
                  </CssVarsProvider>
                </Grid>
                <Grid xl={12} md={12} sm={12} xs={12}>
                  <CssVarsProvider>
                    <FormControl variant="filled" fullWidth>
                      <InputLabel id="lockCalendar-label" shrink>Activo</InputLabel>
                      <Select
                        labelId="enabled-label"
                        id="enabled"
                        name="enabled"
                        label="enabled"
                        value={formik.values.active}
                        onBlur={formik.handleBlur}
                        onChange={handleChangeActive}
                      >
                        <MenuItem key="true" value="true">Si</MenuItem>
                        <MenuItem key="false" value="false">No</MenuItem>
                      </Select>
                      {formik.touched.active && formik.errors.active ? (
                        <span className="Error-alert">{formik.errors.active}</span>
                      ) : null}
                    </FormControl>
                  </CssVarsProvider>
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
              </Stack>
            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoryForm;
